import { Fragment, PureComponent, ReactNode } from 'react';
import { getPtFullNameWithSalutation, Patient } from '../../store/types/patient';
import { COMMUNICATION, RequestData, Response } from '../../api/pushMedicalCertificate';
import { getSelectedClinicId, getTemplateV2 } from '../../store/idbHandler';
import { captureErrorString } from '../../pages/captureError';
import AppLoader from './AppLoader';
import SaveAsTemplateCr from './SaveAsTemplateCr';
import { track } from '../../tracking';
import { utf8ToBase64 } from '../../pages/utils';

type Props = {
    patient: Patient;
    certId: string;
    content: string;
    originalContent: string;
    heading?: string;
    onContinue: () => void;
    withPrintRxConfig?: boolean;
    params?: { [k: string]: string };
    pushApi: (docid: string, data: RequestData) => Promise<Response>;
    type: 'mcert' | 'consent';
    clinicId?: string;
    docid: string;
};

type State = {
    pushState: 'pushing' | 'pushed' | 'error';
    url?: string;
    isSent?: boolean;
    communication?: COMMUNICATION;
};

class CompleteCr extends PureComponent<Props, State> {
    EDITOR = 'EDITOR';
    PREVIEW_IFRAME_ID = 'PREVIEW_IFRAME_ID';
    state: State = {
        pushState: 'pushing',
    };

    componentDidMount = async (): Promise<void> => {
        const { docid } = this.props;

        if (!this.props.patient.id) {
            captureErrorString(
                'CompleteCr: patient.id is null ' + JSON.stringify(this.props.patient),
            );
        }
        const selectedClinicId = this.props.clinicId ?? getSelectedClinicId();
        const renderPdfConfig =
            this.props?.withPrintRxConfig && selectedClinicId
                ? await getTemplateV2(docid, selectedClinicId, 'PRINT')
                : undefined;

        const renderPdfSendConfig =
            this.props?.withPrintRxConfig && selectedClinicId
                ? await getTemplateV2(docid, selectedClinicId, 'SEND')
                : undefined;

        this.props
            .pushApi(docid, {
                patientid: this.props.patient.id || this.props.patient.localId,
                clinicid: getSelectedClinicId() || '',
                contentObject: {
                    heading: utf8ToBase64(this.props.heading || ''),
                    description: utf8ToBase64(this.props.content),
                },
                params: this.props.params,
                renderPdfConfig: renderPdfConfig,
                renderPdfSendConfig: renderPdfSendConfig,
            })
            .then((res) => {
                this.setState({
                    pushState: 'pushed',
                    url: res.url,
                    isSent: res.isSent,
                    communication: res.communication,
                });
            })
            .catch((e) => {
                console.error(e);
                alert('Error saving medical certificate, please try again');
                this.props.onContinue();
            });
    };

    onContinue = (): void => {
        const { onContinue } = this.props;
        onContinue();
    };

    onPrint = (): void => {
        const { url } = this.state;
        const { type } = this.props;
        track('OPTION_SELECT', { option: 'print', context: type, url });
        const t = document.getElementById(this.PREVIEW_IFRAME_ID) as HTMLIFrameElement;
        if (t && t.contentWindow) {
            t.contentWindow.print();
            return;
        }
        captureErrorString('CompleteCr: onPrint: PREVIEW_IFRAME not found ' + url);
        window.open(url);
    };

    onDownload = (): void => {
        const { url } = this.state;
        const { type } = this.props;
        track('OPTION_SELECT', { option: 'download', context: type, url });
        window.open(url);
    };

    render(): ReactNode {
        const { patient, heading, originalContent, certId, type } = this.props;
        const { pushState, url, isSent, communication } = this.state;

        return (
            <Fragment>
                {pushState === 'pushing' && (
                    <div className="flex flex-1 justify-center items-center">
                        <AppLoader />
                    </div>
                )}
                {pushState === 'pushed' && (
                    <div className="flex-1 flex px-12">
                        <div className="w-40p py-30">
                            <div className="flex items-start space-x-10">
                                <svg
                                    className="w-18 h-18 min-w-18 mt-3"
                                    viewBox="0 0 16 16"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M8 0C3.5625 0 0 3.59375 0 8C0 12.4375 3.5625 16 8 16C12.4062 16 16 12.4375 16 8C16 3.59375 12.4062 0 8 0ZM11.5938 6.625L7.59375 10.625C7.4375 10.8125 7.21875 10.875 7 10.875C6.75 10.875 6.53125 10.8125 6.375 10.625L4.375 8.625C4.03125 8.28125 4.03125 7.75 4.375 7.40625C4.71875 7.0625 5.25 7.0625 5.59375 7.40625L7 8.78125L10.375 5.40625C10.7188 5.0625 11.25 5.0625 11.5938 5.40625C11.9375 5.75 11.9375 6.28125 11.5938 6.625Z"
                                        fill="#1F832D"
                                    ></path>
                                </svg>
                                <p>
                                    Document has been successfully {isSent ? 'sent' : 'saved'}{' '}
                                    {isSent && (
                                        <Fragment>
                                            to{' '}
                                            <span className="font-700">
                                                {getPtFullNameWithSalutation(patient)}
                                            </span>{' '}
                                            ({patient.personal.phone?.n})
                                        </Fragment>
                                    )}
                                </p>
                                {/* <button className="whitespace-nowrap text-darwin-primary-500 font-700">
                            Change Number
                        </button> */}
                            </div>
                            <div className="flex mt-40">
                                <div
                                    onClick={this.onPrint}
                                    className="w-100 space-y-14 cursor-pointer"
                                >
                                    <div className="flex items-center justify-center w-40 h-40 m-auto bg-darwin-neutral-50 rounded-full">
                                        <svg
                                            className="w-16 h-16 fill-darwin-accent-examination-findings-sky-blue-900"
                                            viewBox="0 0 16 16"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M3.46875 1.5H11.4062L12.5 2.5625V5H14V2.5625C14 2.1875 13.8125 1.78125 13.5312 1.5L12.5 0.46875C12.2188 0.1875 11.8125 0 11.4375 0H3.46875C2.65625 0 2 0.6875 2 1.5V4.09375V5H3.5L3.46875 1.5ZM13.75 6H2.25C1 6 0 7.03125 0 8.25V11.75C0 12.1875 0.3125 12.5 0.75 12.5H2.5V15C2.5 15.5625 2.9375 16 3.5 16H12.5C13.0312 16 13.5 15.5625 13.5 15V12.5H15.25C15.6562 12.5 16 12.1875 16 11.75V8.25C16 7.03125 14.9688 6 13.75 6ZM12 14.5H4V11.5H12V14.5ZM14.5 11H13.5C13.5 10.4688 13.0312 10 12.5 10H3.5C2.9375 10 2.5 10.4688 2.5 11H1.5V8.25C1.5 7.84375 1.8125 7.5 2.25 7.5H13.75C14.1562 7.5 14.5 7.84375 14.5 8.25V11Z"></path>
                                        </svg>
                                    </div>
                                    <p className="text-center font-700 px-10">Print</p>
                                </div>
                                <div className="flex items-center mx-10">
                                    <svg
                                        className="w-1 h-32 fill-darwin-neutral-100"
                                        viewBox="0 0 1 32"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <rect
                                            x="0.25"
                                            y="0.25"
                                            width="0.5"
                                            height="31.5"
                                            rx="0.25"
                                            stroke="#E4E5ED"
                                            stroke-width="0.5"
                                        ></rect>
                                    </svg>
                                </div>
                                <div
                                    onClick={this.onDownload}
                                    className="w-100 space-y-14 cursor-pointer"
                                >
                                    <div className="flex items-center justify-center w-40 h-40 m-auto bg-darwin-neutral-50 rounded-full">
                                        <svg
                                            className="w-14 h-18 fill-darwin-accent-mauve-800"
                                            viewBox="0 0 12 18"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M11.5625 1.09375C11.2812 0.96875 10.9688 1 10.75 1.1875L9.53125 2.25L8.5 1.25C8.25 0.96875 7.84375 0.9375 7.53125 1.15625L6 2.34375L4.4375 1.15625C4.125 0.9375 3.71875 0.96875 3.46875 1.25L2.4375 2.25L1.21875 1.1875C1 1 0.6875 0.96875 0.40625 1.09375C0.15625 1.21875 0 1.46875 0 1.78125V16.25C0 16.5312 0.15625 16.8125 0.40625 16.9062C0.6875 17.0312 1 17 1.21875 16.8125L2.4375 15.75L3.46875 16.7812C3.71875 17.0312 4.125 17.0625 4.4375 16.8438L6 15.6875L7.53125 16.8438C7.84375 17.0625 8.25 17.0312 8.5 16.7812L9.53125 15.75L10.75 16.8125C10.875 16.9375 11.0625 17 11.25 17C11.3438 17 11.4375 16.9688 11.5625 16.9062C11.8125 16.8125 12 16.5625 12 16.25V1.78125C12 1.46875 11.8125 1.21875 11.5625 1.09375ZM10.5 14.625L9.96875 14.1875C9.6875 13.9375 9.21875 13.9688 8.96875 14.25L7.90625 15.2812L6.4375 14.1562C6.3125 14.0625 6.15625 14 6 14C5.8125 14 5.65625 14.0625 5.53125 14.1562L4.0625 15.2812L3 14.25C2.75 13.9688 2.28125 13.9375 2 14.1875L1.5 14.625V3.40625L2 3.84375C2.28125 4.09375 2.75 4.0625 3 3.78125L4.0625 2.75L5.53125 3.875C5.8125 4.0625 6.15625 4.0625 6.4375 3.875L7.90625 2.75L8.96875 3.78125C9.21875 4.0625 9.6875 4.09375 9.96875 3.84375L10.5 3.40625V14.625ZM8.75 6H3.25C2.96875 6 2.75 6.25 2.75 6.5C2.75 6.75 2.96875 6.96875 3.25 6.96875H8.75C9 6.96875 9.25 6.75 9.25 6.5C9.25 6.25 9 6 8.75 6ZM8.75 11H3.25C2.96875 11 2.75 11.2188 2.75 11.4688C2.75 11.75 2.96875 11.9375 3.25 11.9375H8.75C9 11.9375 9.25 11.7188 9.25 11.4688C9.25 11.25 9 11 8.75 11ZM8.75 8.5H3.25C2.96875 8.5 2.75 8.75 2.75 9C2.75 9.25 2.96875 9.46875 3.25 9.46875H8.75C9 9.46875 9.25 9.25 9.25 9C9.25 8.75 9 8.5 8.75 8.5Z"></path>
                                        </svg>
                                    </div>
                                    <p className="text-center font-700 px-10">Download</p>
                                </div>
                            </div>
                            <div className="p-16">
                                <SaveAsTemplateCr
                                    heading={heading || ''}
                                    content={originalContent}
                                    certId={certId}
                                    type={type}
                                />
                            </div>
                        </div>
                        <div className="w-60p">
                            <div className="h-full w-full">
                                <iframe
                                    id={this.PREVIEW_IFRAME_ID}
                                    src={`/pdfjs-3/web/viewer.html?file=${url}#zoom=65`}
                                    className="flex-1 w-full h-full"
                                ></iframe>
                            </div>
                        </div>
                    </div>
                )}
                <div className="p-16 flex justify-end bg-darwin-neutral-50">
                    <button
                        onClick={this.onContinue}
                        className="border px-12 py-8 text-white font-700 rounded-4 bg-darwin-primary-500"
                    >
                        Done
                    </button>
                </div>
            </Fragment>
        );
    }
}

export default CompleteCr;
